/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable no-undef */
import React, { useCallback, useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import setAuth from '@/redux/actions/authAction';
import useAllLang from '@/shared/hooks/useAllLang';
import { clearAllAuthTokens } from '@/shared/utils/authStorage';

import Footer from './Footer';
import HeaderNav from './HeaderNav';
import MenuDrawer from './MenuDrawer';

const PageLayout = () => {
    const { t, i18n } = useTranslation('common');
    const nav = useNavigate();
    const dispatch = useDispatch();

    const loading = useSelector((state) => state.loading);
    const location = useLocation();

    const [curPath, setCurPath] = useState();
    const [logo, setLogo] = useState(null);
    const [banner, setBanner] = useState(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    useEffect(() => {
        setCurPath(location.pathname);
        if (import.meta.env.VITE_APP_ENV === 'prod') {
            ReactGA.send({ hitType: 'pageview', page: location.pathname });
        }
    }, [location.pathname]);

    const [allLangs, langName, changeLanguage] = useAllLang();
    const [langOption, setlangoption] = useState([]);

    useEffect(() => {
        let inLang = [];
        allLangs.forEach((e, i) => {
            if (i18n.language !== e.locale) {
                inLang.push({ key: e.locale, label: e.name });
            }
        });
        setlangoption(inLang);
    }, [i18n.language, allLangs]);

    useEffect(() => {
        let logo = localStorage.getItem('lg');
        setLogo(logo);
    });

    useEffect(() => {
        let logo = localStorage.getItem('bn');
        setBanner(logo);
    });

    const logout = () => {
        dispatch(setAuth(false));
        clearAllAuthTokens();
        nav('/');
    };

    const onChgLang = ({ key }) => {
        changeLanguage(key);
    };

    const goToPage = (r) => {
        setMobileMenuOpen(false);
        nav(r);
    };

    // Scroll logic for header hide/show
    const [y, setY] = useState(window.scrollY);

    const handleScroll = useCallback(
        (e) => {
            const header = document.getElementById('site-header-v2');
            const headerMobile = document.getElementById('site-header-mobile-v2');

            const window = e.currentTarget;

            if (window.scrollY === 0) {
                header?.classList.remove('hide');
                headerMobile?.classList.remove('hide');
            } else {
                if (y > window.scrollY) {
                    header?.classList.remove('hide');
                    headerMobile?.classList.remove('hide');
                } else if (y < window.scrollY) {
                    if (window.scrollY > 115) {
                        header?.classList.add('hide');
                        headerMobile?.classList.add('hide');
                    }
                }
            }
            setY(window.scrollY);
        },
        [y],
    );

    useEffect(() => {
        setY(window.scrollY);
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [handleScroll]);

    const profileMenuItems = [
        {
            label: 'My Profile',
            key: '/member/profile',
            icon: (
                <svg className="w-4 h-4 mr-3 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    ></path>
                </svg>
            ),
        },
        {
            label: 'Change Password',
            key: '/member/change-password',
            icon: (
                <svg className="w-4 h-4 mr-3 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M15.75 5.25a3 3 0 0 1 3 3m3 0a6 6 0 0 1-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1 1 21.75 8.25Z"
                    />
                </svg>
            ),
        },
    ];

    const onClickProfile = (key) => {
        if (key === 'logout') {
            logout();
        } else {
            goToPage(key);
        }
    };

    if (loading.loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">{t(`label.${loading.tip}`)}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Mobile Header */}
            <header
                id="site-header-mobile-v2"
                className="lg:hidden bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50 transition-transform duration-300"
            >
                <div className="px-4 py-3">
                    <div className="flex items-center justify-between">
                        {/* Logo and Brand Name */}
                        <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 text-blue-600">
                                {logo ? (
                                    <img
                                        src={logo}
                                        alt="Logo"
                                        className="w-full h-full cursor-pointer"
                                        onClick={() => goToPage('/member/register-product')}
                                    />
                                ) : (
                                    <svg
                                        viewBox="0 0 48 48"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="cursor-pointer"
                                        onClick={() => goToPage('/member/register-product')}
                                    >
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M24 18.4228L42 11.475V34.3663C42 34.7796 41.7457 35.1504 41.3601 35.2992L24 42V18.4228Z"
                                            fill="currentColor"
                                        ></path>
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M24 8.18819L33.4123 11.574L24 15.2071L14.5877 11.574L24 8.18819ZM9 15.8487L21 20.4805V37.6263L9 32.9945V15.8487ZM27 37.6263V20.4805L39 15.8487V32.9945L27 37.6263ZM25.354 2.29885C24.4788 1.98402 23.5212 1.98402 22.646 2.29885L4.98454 8.65208C3.7939 9.08038 3 10.2097 3 11.475V34.3663C3 36.0196 4.01719 37.5026 5.55962 38.098L22.9197 44.7987C23.6149 45.0671 24.3851 45.0671 25.0803 44.7987L42.4404 38.098C43.9828 37.5026 45 36.0196 45 34.3663V11.475C45 10.2097 44.2061 9.08038 43.0155 8.65208L25.354 2.29885Z"
                                            fill="currentColor"
                                        ></path>
                                    </svg>
                                )}
                            </div>
                        </div>

                        {/* Right Side - Profile and Menu */}
                        <div className="flex items-center space-x-3">
                            {/* Profile Avatar */}
                            <div className="relative group">
                                <button className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 active:text-blue-600 rounded-lg active:bg-gray-100 transition-colors touch-manipulation">
                                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-medium text-sm">
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                                        </svg>
                                    </div>
                                </button>

                                <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible transition-all duration-200 z-50">
                                    <div className="py-2">
                                        {profileMenuItems.map((item) => (
                                            <button
                                                key={item.key}
                                                onClick={() => onClickProfile(item.key)}
                                                className="w-full flex items-center px-4 py-3 text-sm text-gray-700 active:bg-blue-50 active:text-blue-600 transition-colors text-left touch-manipulation"
                                            >
                                                {item.icon}
                                                {item.label}
                                            </button>
                                        ))}
                                        <button
                                            onClick={() => onClickProfile('logout')}
                                            className="w-full flex items-center px-4 py-3 text-sm text-red-600 active:bg-red-50 transition-colors text-left touch-manipulation"
                                        >
                                            <svg
                                                className="w-4 h-4 mr-3"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth="2"
                                                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                                                ></path>
                                            </svg>
                                            Logout
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Hamburger Menu Button */}
                            <button
                                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                className="p-2 rounded-md text-gray-600 hover:text-blue-600 active:text-blue-600 hover:bg-gray-100 active:bg-gray-100 transition-colors touch-manipulation"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M4 6h16M4 12h16M4 18h16"
                                    ></path>
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Desktop Header */}
            <header
                id="site-header-v2"
                className="hidden lg:block bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50 transition-transform duration-300"
            >
                <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16 gap-10">
                        {/* Logo Section */}
                        <div className="flex items-center">
                            {logo && (
                                <img
                                    src={logo}
                                    alt="Logo"
                                    className="h-10 cursor-pointer"
                                    onClick={() => goToPage('/member/register-product')}
                                />
                            )}
                        </div>

                        {/* Desktop Navigation */}
                        <div className="flex-1 mx-8">
                            <HeaderNav goToPage={goToPage} curPath={curPath} />
                        </div>

                        {/* Right Side - Language & Profile */}
                        <div className="flex items-center space-x-4">
                            {/* Language Dropdown - Hidden for now */}
                            <div className="relative group" style={{ display: 'none' }}>
                                <button className="flex items-center space-x-1 px-3 py-2 text-sm text-gray-700 hover:text-blue-600 rounded-lg hover:bg-gray-100 transition-colors">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"
                                        ></path>
                                    </svg>
                                </button>
                            </div>

                            {/* Profile Dropdown */}
                            <div className="relative group">
                                <button className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 hover:text-blue-600 active:text-blue-600 rounded-lg hover:bg-gray-100 active:bg-gray-100 transition-colors touch-manipulation">
                                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-medium text-sm">
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                                        </svg>
                                    </div>
                                </button>

                                <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible transition-all duration-200 z-50">
                                    <div className="py-2">
                                        {profileMenuItems.map((item) => (
                                            <button
                                                key={item.key}
                                                onClick={() => onClickProfile(item.key)}
                                                className="w-full flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 active:bg-blue-50 active:text-blue-600 transition-colors text-left touch-manipulation"
                                            >
                                                {item.icon}
                                                {item.label}
                                            </button>
                                        ))}
                                        <button
                                            onClick={() => onClickProfile('logout')}
                                            className="w-full flex items-center px-4 py-3 text-sm text-red-600 hover:bg-red-50 active:bg-red-50 transition-colors text-left touch-manipulation"
                                        >
                                            <svg
                                                className="w-4 h-4 mr-3"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth="2"
                                                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                                                ></path>
                                            </svg>
                                            Logout
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content with Banner Underlay */}
            <div className="relative flex-1">
                {/* Banner Underlay */}
                {banner != null ? (
                    <div
                        className="absolute top-0 left-0 right-0 h-full bg-no-repeat bg-center bg-cover z-0"
                        style={{
                            backgroundImage: `url(${banner})`,
                        }}
                    >
                        <div className="absolute inset-0 bg-black bg-opacity-40"></div>
                    </div>
                ) : (
                    <div className="absolute top-0 left-0 right-0 h-[300px] bg-gradient-to-r from-blue-500 to-blue-600 z-0">
                        <div className="absolute inset-0 bg-black bg-opacity-20"></div>
                    </div>
                )}

                {/* Main Content */}
                <main className="relative z-10">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                        <Outlet />
                    </div>
                </main>
            </div>

            {/* Footer */}
            <footer className="mt-auto">
                <Footer />
            </footer>

            {/* Mobile Menu Drawer */}
            <MenuDrawer
                onClose={() => setMobileMenuOpen(false)}
                visible={mobileMenuOpen}
                goToPage={goToPage}
                logout={logout}
                curPath={curPath}
            />

            {/* Custom CSS for smooth hide animation */}
            <style>{`
        .hide {
          transform: translateY(-100%);
        }
      `}</style>
        </div>
    );
};

export default PageLayout;
